import { forwardRef, useImperativeHandle } from 'react';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { generateRange, getDaysInMonth, getInputValue } from './utils';
import classNames from 'classnames';
import { useDateStore } from './hook/useDateStore';
import { dateStore } from './store';
import { CustomCalendarProps, Options } from './types';
import { useCustomCalendar } from './hook/useCustomCalendar';
import dayjs from 'dayjs';
import { DropdownCalendar } from './components/DropdownCalendar';

dayjs.extend(localeData);
dayjs.extend(customParseFormat);

const yearsBack = 100;
const majorityOfAge = 18;

export const CustomCalendar = forwardRef<HTMLInputElement, CustomCalendarProps>(
  (
    {
      name,
      label,
      dateFormat = 'YYYY-MM-DD',
      isMonthsShort,
      placeholderDay,
      placeholderMonth,
      placeholderYear,
      disabled,
      labelClassName,
      classNameDropdown,
      classNameContainer,
      tabIndex = 0,
      ...restProps
    },
    ref
  ) => {
    const { year, month, day, today } = useDateStore();

    /** Определяем массив месяцев. Если isMonthsShort истинно, используем сокращенные названия месяцев (`Jan`, `Feb`...), иначе - полные названия (`January`, `February`...). */
    const months = isMonthsShort ? dayjs.monthsShort() : dayjs.months();

    /** Генерируем массив лет, начиная от today.year() - yearsBack до текущего года, и затем разворачиваем его.*/
    const years = generateRange(today.year() - yearsBack, today.year() - majorityOfAge).reverse();

    /** Генерируем массив чисел, представляющих дни в месяце, от 1 до количества дней в месяце.
     Если год не указан, используем текущий год. Если месяц не указан, используем январь (месяц 0). */
    const daysInMonth: number[] = generateRange(
      1,
      getDaysInMonth(year || today.year(), month ?? 0)
    );

    const handleDaySelect = (selectedDay: Options) => {
      dateStore.setState({ day: Number(selectedDay) });
    };

    const handleMonthSelect = (selectedMonth: Options) => {
      if (selectedMonth) {
        dateStore.setState({ month: months.indexOf(String(selectedMonth)) });
      }
    };

    const handleYearSelect = (selectedYear: Options) => {
      dateStore.setState({ year: Number(selectedYear) });
    };

    const { inputRef } = useCustomCalendar(restProps);

    useImperativeHandle(ref, (): HTMLInputElement => {
      return inputRef.current as HTMLInputElement;
    });

    const selectedMonth = month !== undefined ? months[month] : undefined;

    return (
      <div className={classNames('grid grid-cols-3 gap-1 w-full grid-areas', classNameContainer)}>
        {label && (
          <label className={classNames('grid-area-label mb-1 block ml-5', labelClassName)}>
            {label}
          </label>
        )}
        <DropdownCalendar
          tabIndex={tabIndex}
          className={classNameDropdown}
          disabled={disabled}
          placeholder={placeholderDay}
          options={daysInMonth}
          selected={day}
          onSelect={handleDaySelect}
        />
        <DropdownCalendar
          tabIndex={tabIndex}
          className={classNameDropdown}
          disabled={disabled}
          placeholder={placeholderMonth}
          options={months}
          selected={selectedMonth}
          onSelect={handleMonthSelect}
        />
        <DropdownCalendar
          tabIndex={tabIndex}
          className={classNameDropdown}
          disabled={disabled}
          placeholder={placeholderYear}
          options={years}
          selected={year}
          onSelect={handleYearSelect}
        />
        <input
          id="inputHidden"
          {...restProps}
          ref={inputRef}
          type="hidden"
          data-value={getInputValue({
            selectedYear: year,
            selectedDay: day,
            selectedMonth: month,
            dateFormat: 'YYYY-MM-DD'
          })}
          name={name}
          value={getInputValue({
            selectedYear: year,
            selectedDay: day,
            selectedMonth: month,
            dateFormat
          })}
        />
      </div>
    );
  }
);

CustomCalendar.displayName = 'CustomCalendar';
